import { CloudDownloadOutlined } from "@ant-design/icons"
import { IncidentInterface } from "@type/workMonitor.types"
import { Modal } from "antd"
import { Image } from "antd-v5"
import { AntdButton } from "components"
import { SignedUrlForImage } from "components/index"
import { DATE_FORMATS } from "config/constants"
import { momentZ } from "config/moment"
import { newTimeTrailShieldLogo } from "images"
import React from "react"
import { reportService } from "services/"

interface IncidentReportModalProps {
	open: boolean
	onClose: () => void
	reportData: IncidentInterface
	componentRef: React.RefObject<HTMLDivElement>
}

const IncidentReportModal: React.FC<IncidentReportModalProps> = ({ open, onClose, reportData, componentRef }) => {
	const getBase64FromImageUrl = async (url: string): Promise<string> => {
		const response = await fetch(url)
		const blob = await response.blob()
		return new Promise((resolve, reject) => {
			const reader = new FileReader()
			reader.onloadend = () => {
				resolve(reader.result as string)
			}
			reader.onerror = reject
			reader.readAsDataURL(blob)
		})
	}
	const [isGenerating, setIsGenerating] = React.useState(false)

	const handleDownload = async () => {
		try {
			setIsGenerating(true)
			if (!reportData) return
			const res = await reportService.generateIncidentReport(reportData)
			const file = new Blob([res.data])
			const link = document.createElement("a")
			link.href = URL.createObjectURL(file)
			link.download = "incident-report.pdf"
			link.click()
			URL.revokeObjectURL(link.href)
		} catch (error) {
			console.error(error)
		} finally {
			setIsGenerating(false)
		}
	}

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			title={
				<div className="flex w-full items-center justify-between">
					<span className="text-lg font-semibold">Incident Report</span>
					<AntdButton
						id="trigger-download"
						className="mr-4"
						icon={<CloudDownloadOutlined />}
						size="small"
						onClick={handleDownload}
					>
						Generate PDF
					</AntdButton>
				</div>
			}
			width={1000}
			bodyStyle={{ padding: 0 }}
		>
			<div ref={componentRef} className="bg-white p-8">
				{/* Header */}
				<div className="flex items-start justify-between border-b-2 border-black pb-6">
					<div className="flex items-start gap-6">
						<div className="flex h-20 w-20 items-center justify-center">
							<img src={newTimeTrailShieldLogo} alt="Logo" className="max-h-full max-w-full" />
						</div>
						<div>
							<h1 className="text-3xl font-bold">INCIDENT REPORT</h1>
							<p className="text-gray-600">{reportData.site.name}</p>
						</div>
					</div>
					<div className=" p-3 text-right">
						<p>
							<span className="font-bold">Incident:</span> {reportData.incident}
						</p>
						<p>
							<span className="font-semibold">Date:</span>{" "}
							{momentZ(reportData.date).format(DATE_FORMATS.DD_MM_YYYY)}
						</p>
						<p>
							<span className="font-semibold">Time:</span>{" "}
							{momentZ(reportData.date).format(DATE_FORMATS.HH_mm)}
						</p>
					</div>
				</div>

				<p className="mt-2 font-semibold text-red-600">CONFIDENTIAL DOCUMENT</p>

				{/* Section: Details of Office*/}
				<div>
					<p>
						<span className="font-semibold">Reported By:</span> {reportData.reportingOfficer.firstName}{" "}
						{reportData.reportingOfficer.lastName}
					</p>

					<p>
						<span className="font-semibold">Security Officer:</span>{" "}
						{reportData.officer ? `${reportData.officer?.firstName} ${reportData.officer?.lastName}` : " -"}
					</p>
				</div>
				{/* Section: Details of Resolution*/}
				<div>
					<p>
						<span className="font-semibold">Resolved By:</span>{" "}
						{reportData.resolvedBy?.firstName || reportData.resolvedBy?.lastName
							? `${reportData.resolvedBy.firstName ?? ""} ${reportData.resolvedBy.lastName ?? ""}`.trim()
							: " -"}
					</p>
					<div className="text-sm text-gray-800">
						<span className="font-semibold">Resolved At:</span>{" "}
						{reportData.incidentState
							? `${momentZ(reportData.resolvedAt).format(
									`${DATE_FORMATS.DD_MM_YYYY} ${DATE_FORMATS.HH_mm}`,
							  )}`
							: "Unresolved"}
					</div>
				</div>

				{/* Section: Details of Incident */}
				<div className="mt-8">
					<h2 className="mb-1 text-lg font-semibold">DETAILS OF INCIDENT</h2>
					{reportData.details?.trim() ? (
						<p className="whitespace-pre-wrap border bg-gray-50 p-3 text-gray-700">{reportData.details}</p>
					) : (
						<p className="border bg-gray-50 p-3 text-gray-500">No details added</p>
					)}
				</div>

				{/* Section: Comments */}
				<div className="mt-8">
					<h2 className="mb-1 text-lg font-semibold">COMMENTS</h2>
					<div className="space-y-3 border bg-gray-50 p-4">
						{reportData.incidentComments && reportData.incidentComments.length > 0 ? (
							reportData.incidentComments.map(comment => (
								<div key={comment._id} className="rounded border border-gray-200 bg-white p-3">
									<p className="text-sm text-gray-800">{comment.comment}</p>
									<div className="mt-1 flex justify-between text-xs text-gray-500">
										<span>
											{comment.author?.firstName} {comment.author?.lastName}
										</span>

										<span>
											{momentZ(comment.createdAt).format(DATE_FORMATS.REPORT_DD_MM_YYYY_HH_mm)}
										</span>
									</div>
								</div>
							))
						) : (
							<p className=" text-gray-500">No comments added</p>
						)}
					</div>
				</div>

				{/* Section: Control & Police Info */}
				<div className="mt-8">
					<h2 className="mb-1 text-lg font-semibold">CONTROL & POLICE INFORMATION</h2>
					<div className="grid grid-cols-3 gap-4 border bg-gray-50 p-3">
						<p>
							<span className="font-semibold">Control Informed:</span>{" "}
							{reportData.controlInformed ? "Yes" : "No"}
						</p>
						<p>
							<span className="font-semibold">Police Requested:</span>{" "}
							{reportData.isPoliceRequested ? "Yes" : "No"}
						</p>
						<p>
							<span className="font-semibold">Police Reference Number:</span>{" "}
							{reportData.reference || "—"}
						</p>
					</div>
				</div>

				{/* Section: Supporting Evidence */}
				<div className="mt-8">
					<h2 className="mb-1 text-lg font-semibold">SUPPORTING EVIDENCE</h2>
					{reportData.images && reportData.images.length > 0 ? (
						<div className="grid grid-cols-4 gap-4 border bg-gray-50 p-6">
							<Image.PreviewGroup>
								{reportData.images.map((imgSrc, index) => (
									<SignedUrlForImage path={imgSrc} key={`evidenceImage_${index}`}>
										{(signedUrl: string) => {
											const [base64, setBase64] = React.useState<string | null>(null)

											React.useEffect(() => {
												getBase64FromImageUrl(signedUrl).then(setBase64)
											}, [signedUrl])

											return base64 ? (
												<div>
													<img
														src={base64}
														alt={`Evidence ${index + 1}`}
														className="max-h-60 w-full rounded object-contain"
													/>
												</div>
											) : (
												<div className="border p-2 italic text-gray-400">Loading image…</div>
											)
										}}
									</SignedUrlForImage>
								))}
							</Image.PreviewGroup>
						</div>
					) : (
						<div className="border border-dashed bg-gray-50 p-6 text-center text-gray-500">
							No evidence uploaded
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="mt-12 flex justify-between border-t pt-4 text-xs text-gray-600">
					<p>
						This document contains confidential information. Distribution restricted to authorized personnel
						only.
					</p>
					<p>{reportData.site.name}</p>
				</div>
			</div>
			{isGenerating && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
					<div className="rounded bg-white px-6 py-4 shadow-lg">
						<p className="text-lg font-semibold text-gray-800">Generating PDF…</p>
					</div>
				</div>
			)}
		</Modal>
	)
}

export default IncidentReportModal
